<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor"),
  title: $.text("Heading", { default: "Gallery" }),
  specialHeading: $.checkbox("Use special heading font", { default: true }),
  intro: $.text("Introduction", { multiline: true }),
  layout: $.select(
    "Layout",
    [
      { label: "Grid", value: "grid" },
      { label: "Carousel", value: "carousel" },
    ],
    { default: "grid" },
  ),
  columns: $.select(
    "Columns",
    [
      { label: "Two", value: 2 },
      { label: "Three", value: 3 },
      { label: "Four", value: 4 },
      { label: "Five", value: 5 },
    ],
    { default: 3 },
  ).if('layout eq "grid"'),
  ratio: $.select(
    "Image ratio",
    [
      { label: "Landscape", value: "4-3" },
      { label: "Wide", value: "16-9" },
      { label: "Square", value: "1" },
      { label: "Portrait", value: "3-4" },
      {
        label: "Natural width, equal height (horizontal strip)",
        value: "natural",
      },
    ],
    { default: "4-3" },
  ),
  naturalHeight: $.select(
    "Image height",
    [
      { label: "Small (200px)", value: "small" },
      { label: "Medium (300px)", value: "medium" },
      { label: "Large (400px)", value: "large" },
    ],
    { default: "medium" },
  ).if('layout eq "carousel" and ratio eq "natural"'),
  showNavigation: $.checkbox("Show navigation buttons", {
    default: true,
  }).if('layout eq "carousel"'),
  autoplay: $.checkbox("Autoplay", {
    default: false,
  }).if('layout eq "carousel"'),
  lightbox: $.checkbox("Open images in lightbox", { default: true }),
  rounded: $.checkbox("Rounded images", { default: true }),
  compact: $.checkbox("Compact spacing", { default: false }),
  surface: $.select(
    "Background",
    [
      { label: "Page background", value: "page" },
      { label: "White surface", value: "surface" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "page" },
  ),
  backgroundImage: $.upload("Background image").if('surface eq "image"'),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if('surface eq "image" and backgroundImage pr'),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    'surface eq "image" and backgroundImage pr',
  ),
  images: $.array(
    "Images",
    defineSchema({
      image: $.upload("Image"),
      alt: $.text("Image description"),
      caption: $.text("Caption"),
    }),
    {
      collapsible: true,
      allowAdd: true,
      allowDelete: true,
      allowReorder: true,
      default: [],
    },
  ),
});
export const EventGallery = defineBlock({
  name: "EventGallery",
  label: "Gallery",
  icon: "photo_library",
  thumbnail: "/block-thumbs/EventMedia.svg",
  category: "Event",
  order: 75,
  schema,
})
</script>
<script setup lang="ts">
import { computed, ref } from "vue";

import { Btn, Dialog, Icon, Image, Swiper, pathKeyToURL, usePrefersReducedMotion } from "@bagelink/vue"

type GalleryItem = { image?: string; alt?: string; caption?: string };


;

const props = withDefaults(
  defineProps<{
    anchor?: string;
    title?: string;
    specialHeading?: boolean;
    intro?: string;
    layout?: "grid" | "carousel" | "strip";
    columns?: number;
    ratio?: "4-3" | "16-9" | "1" | "3-4" | "natural";
    naturalHeight?: "small" | "medium" | "large";
    showNavigation?: boolean;
    autoplay?: boolean;
    lightbox?: boolean;
    rounded?: boolean;
    compact?: boolean;
    surface?: "page" | "surface" | "brand" | "dark" | "image";
    backgroundImage?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    dimBackground?: boolean;
    images?: GalleryItem[];
  }>(),
  {
    anchor: "",
    title: "Gallery",
    specialHeading: true,
    intro: "",
    layout: "grid",
    columns: 3,
    ratio: "4-3",
    naturalHeight: "medium",
    showNavigation: true,
    autoplay: false,
    lightbox: true,
    rounded: true,
    compact: false,
    surface: "page",
    backgroundImage: "",
    backgroundFit: "cover",
    dimBackground: false,
    images: () => [],
  },
);

const selected = ref<GalleryItem>();
const selectedIndex = ref(0);
const open = ref(false);
const prefersReducedMotion = usePrefersReducedMotion();
const validImages = computed(() => props.images.filter((item) => item.image));
const hasImages = computed(() => validImages.value.length > 0);
const displayedImages = computed<GalleryItem[]>(() =>
  hasImages.value
    ? validImages.value
    : [
        { alt: "Gallery image placeholder" },
        { alt: "Gallery image placeholder" },
        { alt: "Gallery image placeholder" },
        { alt: "Gallery image placeholder" },
      ],
);
const surfaceClass = computed(
  () =>
    ({
      page: "bg-bg",
      surface: "bg-box",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "color-white keep-white",
    })[props.surface],
);
const sectionStyle = computed(() =>
  props.surface === "image" && props.backgroundImage
    ? {
        backgroundImage: `${props.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize:
          props.backgroundFit === "fill" ? "100% 100%" : props.backgroundFit,
      }
    : undefined,
);
const isCarousel = computed(
  () => props.layout === "carousel" || props.layout === "strip",
);
const galleryClass = computed(
  () =>
    `grid grid-wrap-${props.columns} gap-${props.compact ? "05" : "1"} m_gap-05`,
);
const naturalHeightClass = computed(
  () =>
    ({
      small: "h-200px",
      medium: "h-300px",
      large: "h-400px",
    })[props.naturalHeight],
);
const naturalCarousel = computed(
  () => isCarousel.value && props.ratio === "natural",
);
const ratioClass = computed(() => {
  if (naturalCarousel.value) return naturalHeightClass.value;
  return `ratio-${props.ratio === "natural" ? "4-3" : props.ratio}`;
});
const figureClass = computed(() => [naturalCarousel.value ? "w-auto" : ""]);
const imageClass = computed(() => [
  "block",
  ratioClass.value,
  naturalCarousel.value ? "w-auto contain" : "w-100p cover",
]);
const carouselBreakpoints = {
  0: { slidesPerView: 1, spaceBetween: 8 },
  640: { slidesPerView: 2, spaceBetween: 12 },
  1024: { slidesPerView: 3, spaceBetween: 16 },
};
const autoplayConfig = computed(() =>
  props.autoplay && !prefersReducedMotion.value
    ? { delay: 5000, disableOnInteraction: true, pauseOnMouseEnter: true }
    : false,
);

function show(item: GalleryItem, index: number) {
  if (!hasImages.value || !props.lightbox || !item.image) return;
  selected.value = item;
  selectedIndex.value = index;
  open.value = true;
}

function step(direction: number) {
  if (!validImages.value.length) return;
  selectedIndex.value =
    (selectedIndex.value + direction + validImages.value.length) %
    validImages.value.length;
  selected.value = validImages.value[selectedIndex.value];
}
</script>

<template>
  <section
    :id="anchor || undefined"
    class="px-2 py-5 m_px-1 m_py-2"
    :class="surfaceClass"
    :style="sectionStyle"
  >
    <div class="w-1200px">
      <h2
        v-if="title"
        class="m-0 txt-48 m_txt-32 line-height-11 m_line-height-12 balance txt-center pb-1"
        :class="{ 'blox-special-heading': specialHeading }"
      >
        {{ title }}
      </h2>
      <p v-if="intro" class="w-700px txt-center pretty pb-2">{{ intro }}</p>
      <Swiper
        v-if="isCarousel"
        :items="displayedImages"
        :slides-per-view="naturalCarousel ? 'auto' : 3"
        :slide-width="naturalCarousel ? 'auto' : undefined"
        :space-between="compact ? 8 : 16"
        :breakpoints="naturalCarousel ? undefined : carouselBreakpoints"
        :navigation="showNavigation && displayedImages.length > 1"
        :pagination="displayedImages.length > 1 ? { clickable: true } : false"
        :loop="hasImages && displayedImages.length > 3"
        :autoplay="autoplayConfig"
        :class="hasImages ? '' : 'opacity-6'"
				class="event-gallery-carousel pt-1"
      >
        <template #default="{ item, index }">
          <figure class="m-0" :class="figureClass">
            <Btn
              v-if="lightbox && item.image"
              flat
              class="p-0 h-auto overflow-hidden"
              :class="[
                naturalCarousel ? 'w-auto' : 'w-100p',
                rounded ? 'radius-1' : '',
              ]"
              :aria-label="
                item.alt || item.caption || `Open image ${index + 1}`
              "
              @click="show(item, index)"
            >
              <img
                :src="pathKeyToURL(item.image) || item.image"
                :alt="item.alt || ''"
                :class="imageClass"
                loading="lazy"
              />
            </Btn>
            <div
              v-else-if="!item.image"
              class="bg-bg border flex-center"
              :class="[
                ratioClass,
                naturalCarousel ? 'w-300px ms-0 me-0' : 'w-100p',
                rounded ? 'radius-1' : '',
              ]"
            >
              <Icon name="image" :size="3" class="color-gray" />
            </div>
            <Image
              v-else
              :path-key="item.image"
              :alt="item.alt || ''"
              :ratio="ratio === 'natural' ? undefined : ratio"
              :fit="ratio === 'natural' ? 'contain' : 'cover'"
              :rounded="rounded"
              :class="naturalCarousel ? `${naturalHeightClass} w-auto` : ''"
            />
            <figcaption v-if="item.caption" class="txt-center txt14 pt-05">
              {{ item.caption }}
            </figcaption>
          </figure>
        </template>
      </Swiper>
      <div v-else :class="[galleryClass, hasImages ? '' : 'opacity-6']">
        <figure
          v-for="(item, index) in displayedImages"
          :key="`${item.image}-${index}`"
          class="m-0"
          :class="figureClass"
        >
          <Btn
            v-if="lightbox && item.image"
            flat
            class="p-0 h-auto overflow-hidden"
            :class="rounded ? 'w-100p radius-1' : 'w-100p'"
            :aria-label="item.alt || item.caption || `Open image ${index + 1}`"
            @click="show(item, index)"
          >
            <img
              v-if="item.image"
              :src="pathKeyToURL(item.image) || item.image"
              :alt="item.alt || ''"
              :class="imageClass"
              loading="lazy"
            />
          </Btn>
          <div
            v-else-if="!item.image"
            class="w-100p bg-bg border flex-center"
            :class="[ratioClass, rounded ? 'radius-1' : '']"
          >
            <Icon name="image" :size="3" class="color-gray" />
          </div>
          <Image
            v-else-if="item.image"
            :path-key="item.image"
            :alt="item.alt || ''"
            :ratio="ratio === 'natural' ? undefined : ratio"
            :fit="ratio === 'natural' ? 'contain' : 'cover'"
            :rounded="rounded"
          />
          <figcaption v-if="item.caption" class="txt-center txt14 pt-05">
            {{ item.caption }}
          </figcaption>
        </figure>
      </div>
    </div>

    <Dialog
      v-model:open="open"
      :title="selected?.caption || selected?.alt || 'Gallery image'"
      width="xl"
    >
      <div class="grid gap-1">
        <img
          v-if="selected?.image"
          :src="pathKeyToURL(selected.image) || selected.image"
          :alt="selected.alt || ''"
          class="w-100p max-h-700px contain"
        />
        <div v-if="validImages.length > 1" class="flex space-between">
          <Btn flat icon="arrow_back" value="Previous" @click="step(-1)" />
          <Btn flat icon-end="arrow_forward" value="Next" @click="step(1)" />
        </div>
      </div>
    </Dialog>
  </section>
</template>
